<template>
	<div class="code-input-group">
		<t-input
			v-for="number in inputsNumbers"
			:ref="el => (codeRefs[number] = el)"
			:value="code[number - 1]"
			:key="number"
			:highlight-error="!!error"
			label=""
			autocomplete="one-time-code"
			inputmode="numeric"
			class="code-input-group__input"
			:class="{ 'code-input-group__input--last': number === length }"
			@input="(value, isDeleted) => codeChanged(value, isDeleted, number)"
			@key-down="onKeyDown($event, number)"
		/>
		<div v-if="error" class="code-input-group__error">
			<img
				class="code-input-group__error__icon"
				:src="errorIcon"
				alt="error icon"
			/>
			<span>{{ error }}</span>
		</div>
	</div>
</template>

<script>
export default {
	name: 'TCodeInputsGroup'
}
</script>

<script setup>
import TInput from '@/components/Ticketing/Controls/TInput.vue'
import {
	computed,
	nextTick,
	onBeforeUnmount,
	onMounted,
	reactive,
	ref,
	watch
} from 'vue'
// Constants
import { KeysCodes } from '@/types/constantsTypes'
const getDefaultCodeState = length => Array.from({ length }, _ => '')
// Icons
import ErrorIcon from '@/assets/images/ic_info_error.svg'
// Emits
const emit = defineEmits(['filled'])
// Props
const { length } = defineProps({
	length: {
		type: Number,
		default: 6
	},

	error: {
		type: String,
		default: ''
	}
})
// Reactive
const state = reactive({
	code: getDefaultCodeState(length)
})
// Refs
const codeRefs = ref({})
const timeout = ref(0)
const isAndroid = ref(false)
// Computed
const inputsNumbers = computed(() => Array.from({ length }, (_, i) => i + 1))
const code = computed(() => state.code)
const isCodeFull = computed(() => !code.value.filter(char => !char).length)
const errorIcon = computed(() => ErrorIcon)
// Hooks
onMounted(() => {
	if ('OTPCredential' in window) {
		// Cancel the WebOTP API if the form is submitted manually.
		const ac = new AbortController()
		// Invoke the WebOTP API
		// Only then, not async await
		navigator.credentials
			.get({
				otp: { transport: ['sms'] },
				signal: ac.signal
			})
			.then(otp => {
				state.code = otp.code.split('')
				ac.abort()
			})
			.catch(err => {
				console.log(err)
				ac.abort()
			})
	}

	const agent = navigator.userAgent.toLowerCase()
	isAndroid.value = agent.indexOf('android') > -1
})

// Watch
watch(
	code,
	newVal => {
		const isFullCode = !newVal.filter(char => !char).length

		const stringCode = state.code.join('')

		if (isFullCode && !(stringCode.length > length)) {
			emit('filled', stringCode)
		}
	},
	{ deep: true }
)

// Hooks
onBeforeUnmount(() => {
	if (timeout.value) clearTimeout(timeout.value)
})

// Methods
const focusFirstField = () => {
	codeRefs.value[1].focusInput()
}

const resetCode = () => {
	state.code = getDefaultCodeState(length)
}

const onKeyDown = (e, number) => {
	const isDeleted =
		e.keyCode === KeysCodes.backSpace || e.keyCode === KeysCodes.deleteKey

	if (isDeleted) {
		if (!state.code[number - 1] && number > 1) {
			const newCode = code.value
			newCode[number - 2] = ''
			state.code = [...newCode]
			codeRefs.value[number - 1].focusInput()
		} else {
			const newCode = code.value
			newCode[number - 1] = ''
			state.code = [...newCode]
		}
	}
}

const codeChangedAndroid = (value, number) => {
	const newCode = code.value
	// Autofill from sms or paste 6 digits in 1 input cases
	if (value.length === length) {
		for (let i = 0; i < value.length; i++) {
			newCode[i] = value[i]
		}

		state.code = [...newCode]

		codeRefs.value[length].focusInput()

		return
	}

	if (value.length === 2) {
		let newVal

		if (newCode[number - 1] === value[0]) {
			newVal = value[1]

			if (value[0] === value[1]) {
				codeRefs.value[number]?.updateComponent()
			}
		} else {
			newVal = value[0]
		}
		nextTick(() => {
			newCode[number - 1] = newVal
			state.code = [...newCode]

			if (number < length) {
				nextTick(() => codeRefs.value[number + 1].focusInput())
			}
		})
	}

	newCode[number - 1] = value
	state.code = [...newCode]

	const isFullCode = !state.code.filter(char => !char).length

	if (number === length) {
		if (isFullCode) return

		const nextEmpty = state.code.findIndex(char => !char)
		codeRefs.value[nextEmpty + 1].focusInput()

		return
	}

	codeRefs.value[number + 1].focusInput()
}

const getNativeInputValue = inputIndex => {
	const inputComponent = codeRefs.value[inputIndex]
	if (!inputComponent || !inputComponent.$el) {
		return ''
	}
	const nativeInputElement = inputComponent.$el.firstChild
	if (!nativeInputElement) {
		return ''
	}
	return nativeInputElement.value || ''
}

const codeNotAndroidChanged = (value, number) => {
	if (timeout.value) {
		clearTimeout(timeout.value)
	}

	// setTimeout is needed for IOS autofill from sms case
	timeout.value = setTimeout(() => {
		const inputsValues = []
		let totalCharsCount = 0
		let nonEmptyInputsCount = 0

		for (let inputIndex = 1; inputIndex <= length; inputIndex += 1) {
			const inputValue = getNativeInputValue(inputIndex)
			inputsValues[inputIndex - 1] = inputValue
			totalCharsCount += inputValue.length
			if (inputValue.length > 0) {
				nonEmptyInputsCount += 1
			}
		}

		const currentInputValueFromDom =
			inputsValues[number - 1] || getNativeInputValue(number)
		const newCode = code.value

		// full code is entered/filled into a single input (all other inputs are empty)
		if (
			currentInputValueFromDom.length === length &&
			nonEmptyInputsCount === 1 &&
			!isCodeFull.value
		) {
			state.code = currentInputValueFromDom.split('')
			codeRefs.value[length]?.focusInput()
			return
		}

		// system autofill across multiple inputs
		if (
			totalCharsCount >= length &&
			nonEmptyInputsCount > 1 &&
			!isCodeFull.value
		) {
			for (let index = 0; index < length; index += 1) {
				const rawValue = inputsValues[index] || ''
				newCode[index] = rawValue ? rawValue.charAt(rawValue.length - 1) : ''
			}

			state.code = [...newCode]
			codeRefs.value[length]?.focusInput()
			return
		}

		// Autofill from sms or paste 6 digits in 1 input cases
		if (
			(value.length === length || currentInputValueFromDom.length === length) &&
			!isCodeFull.value
		) {
			const actualValue =
				currentInputValueFromDom.length === length
					? currentInputValueFromDom
					: value

			for (let i = 0; i < length; i += 1) {
				newCode[i] = actualValue[i] || ''
			}

			state.code = [...newCode]

			codeRefs.value[length].focusInput()

			return
		}

		if (value.length === 2) {
			let newVal

			if (newCode[number - 1] === value[0]) {
				newVal = value[1]

				if (value[0] === value[1]) {
					codeRefs.value[number]?.updateComponent()
				}
			} else {
				newVal = value[0]
			}
			nextTick(() => {
				newCode[number - 1] = newVal
				state.code = [...newCode]

				if (number < length) {
					nextTick(() => codeRefs.value[number + 1].focusInput())
				}
			})
		}

		newCode[number - 1] = value
		state.code = [...newCode]

		const isFullCode = !state.code.filter(char => !char).length

		if (number === length) {
			if (isFullCode) return

			const nextEmpty = state.code.findIndex(char => !char)
			codeRefs.value[nextEmpty + 1].focusInput()

			return
		}

		codeRefs.value[number + 1].focusInput()
	}, 20)
}

const codeChanged = (value, isDeleted, number) => {
	if (isDeleted) return

	if (isAndroid.value) {
		codeChangedAndroid(value, number)
	} else {
		codeNotAndroidChanged(value, number)
	}
}

defineExpose({
	focusFirstField,
	resetCode
})
</script>

<style scoped lang="scss">
@import './src/assets/styles/variables';

.code-input-group {
	position: relative;
	margin: 0 auto;
	display: flex;
	align-items: center;
	width: fit-content;

	&__input {
		margin-right: 10px;
		width: 48px;

		/deep/ .form__field__input {
			padding-right: 4px;
			padding-left: 4px;
			font-size: 24px;
			line-height: 40px;
			text-align: center;
		}

		&--last {
			margin-right: 0;
		}
	}

	&__error {
		position: absolute;
		display: flex;
		align-items: center;
		bottom: 0;
		right: 50%;
		color: $natural-red !important;
		font-size: 14px;
		line-height: 19px;
		transform: translateX(50%);

		&__icon {
			margin-right: 6px;
		}
	}
}
</style>
